import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    BoxedAccordion,
    BoxedAccordionItem,
    Callout,
    Row,
    TextField,
    ButtonPrimary,
    IconCheckedRegular,
    IconInformationUserRegular,
    Stack,
    ButtonFixedFooterLayout,
    skinVars,
    RadioGroup,
    RowList,
    PhoneNumberField,
    TextLink,
    Drawer,
    IconCheckedFilled,
} from '@telefonica/mistica';
import { t } from 'i18next';
import './index.scss';
import HeaderComponent from '../../../../components/HeaderComponent';
import { useNavigate } from 'react-router-dom';
import { getAllProducts } from '../../../../services/ProductService';
import { validateDocumentId, validateTextLength } from '../../../../utils/dataTransformation/validateData';
import { IBundle } from '../../../../utils/store/model/store.model';
import { getDocumentValidation } from '../../../../services/OwnershipChangeService';
import { changeOwnershipValidationRequest, OwnerData } from '../../../../models/OwnershipChange';
import { useChangeOwnershipContext } from '../../../../context/change-ownership-context/ChangeOwnershipContext';
import { useGlobalContext } from '../../../../context/global-context/GlobalContext';
import { PRODUCT_ICONS } from '../../utils/contants';
import { formatMobileNumber } from '../../../../utils/dataTransformation/unitsConversion';

export interface FormattedProductData {
    id: string;
    bundleId?: string;
    name: string;
    number: string;
    isBundle?: boolean;
    type: 'fiber' | 'mobile' | 'bundle';
    offeringId: string;
}

const ProductSelection: React.FC = () => {
    const navigate = useNavigate();
    const { context, setContext } = useGlobalContext();
    const {
        owner,
        setOwner,
        selectedProduct,
        setSelectedProduct,
        setPartyRoleId,
        setPartyRoleType,
        setIsOpenOnboarding,
    } = useChangeOwnershipContext();
    const [validForm, setValidForm] = useState(false);
    const [productsData, setProductsData] = useState<FormattedProductData[]>([]);
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedBundleDetails, setSelectedBundleDetails] = useState<{
        name: string;
        items: any[];
        id: string;
        offeringId: string;
    } | null>(null);
    const validationTimeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        (async () => {
            await fetcProductsData();
        })();
    }, []);

    useEffect(() => {
        if (context?.bundles.length || context?.fibers.length || context?.mobiles.length) {
            const formattedData = formatProductsData();
            setProductsData(formattedData);
        }
    }, [context]);

    const checkValidations = useCallback(() => {
        const isNameValid = validateTextLength(owner.name, 0);
        const isFirstSurnameValid = validateTextLength(owner.firstSurname, 0);
        const isDocumentValid = validateDocumentId(owner.documentNumber).valid;
        const isPhoneValid = validateTextLength(owner.contactPhone, 0);

        return (
            !!selectedProduct.productId &&
            !!selectedProduct.productOfferingId &&
            isNameValid &&
            isFirstSurnameValid &&
            isDocumentValid &&
            isPhoneValid
        );
    }, [selectedProduct, owner]);

    useEffect(() => {
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        validationTimeoutRef.current = setTimeout(() => {
            setValidForm(checkValidations());
        }, 300);

        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, [selectedProduct, owner, checkValidations]);

    const fetcProductsData = async () => {
        try {
            const response = await getAllProducts();
            setContext(response.data);
        } catch (error) {
            console.error(error);
            setContext({
                typeUser: null,
                bundles: [],
                fibers: [],
                mobiles: [],
                rentings: [],
                consumptions: null,
            });
        }
    };

    const formatProductsData = () => {
        const { bundles, fibers, mobiles } = context!;
        const formattedFibers = fibers.map((fiber) => ({
            id: fiber.productId,
            name: fiber.name,
            number: fiber.number,
            type: 'fiber' as const,
            offeringId: fiber.offeringId,
        }));
        const formattedMobiles = mobiles.map((mobile) => ({
            id: mobile.productId,
            name: mobile.name,
            number: mobile.number,
            type: 'mobile' as const,
            offeringId: mobile.offeringId,
        }));
        const formattedBundles = (bundles as unknown as IBundle[]).map((bundle) => ({
            id: bundle.mainMobile.productId,
            bundleId: bundle.bundleId,
            name: bundle.name,
            number: [
                bundle.fiber.number,
                bundle.mainMobile.number,
                ...bundle.additionalMobiles.map((mobile) => mobile.number),
            ].join(', '),
            isBundle: true,
            type: 'bundle' as const,
            offeringId: bundle.mainMobile.offeringId,
        }));
        return [...formattedFibers, ...formattedMobiles, ...formattedBundles];
    };

    const getBundleDetails = (bundleId: string) => {
        const selectedBundle = (context?.bundles as unknown as IBundle[]).find(
            (bundle: IBundle) => bundle.bundleId === bundleId,
        );
        const selectedBundleFormatted = {
            name: selectedBundle?.name!,
            items: [selectedBundle?.fiber, selectedBundle?.mainMobile, ...(selectedBundle?.additionalMobiles || [])],
            id: selectedBundle?.mainMobile.productId!,
            offeringId: selectedBundle?.mainMobile.offeringId!,
        };

        setSelectedBundleDetails(selectedBundleFormatted);
        setOpenDetail(true);
    };

    const onChangeHolder = (field: keyof OwnerData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        if (field === 'contactPhone') {
            value = value.replace(/\s+/g, '');
        }
        if (field === 'documentNumber') {
            const docType = validateDocumentId(value).type;
            setOwner((prev) => ({
                ...prev,
                documentNumber: value,
                documentType: docType,
            }));
        } else {
            setOwner((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const navigationBackHandler = () => navigate('/profile/personal-data');

    const handleSubmit = async () => {
        const normalizedDocumentNumber = owner.documentNumber.toUpperCase();
        const { type: currentDocumentType } = validateDocumentId(normalizedDocumentNumber);

        const data: changeOwnershipValidationRequest = {
            documentNumber: normalizedDocumentNumber,
            documentType: currentDocumentType,
            name: owner.name,
            firstSurname: owner.firstSurname,
            secondSurname: owner.secondSurname,
            contactMedia: owner.contactPhone,
        };

        try {
            const response = await getDocumentValidation(data);
            setPartyRoleId(response.data.partyRoleId);
            setPartyRoleType(response.data.partyRoleType);
            setIsOpenOnboarding(response.data.isOpenOnboarding);
            navigate('/change-ownership/contact-media');
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className='product-selection-page'>
            <div className='product-selection-page__header'>
                <HeaderComponent
                    title={t('ChangeOfOwnership')}
                    navigationBarOnBack={navigationBackHandler}
                    breadcrumbs={[
                        { title: t('profileTitle'), url: '/profile' },
                        { title: t('personalDataTitle2'), url: '/profile/personal-data' },
                    ]}
                />
            </div>
            <div className='product-selection-page__content'>
                <ButtonFixedFooterLayout
                    button={
                        <ButtonPrimary disabled={!validForm} onPress={handleSubmit}>
                            {t('continue')}
                        </ButtonPrimary>
                    }
                >
                    <div className='product-selection-page__accordion product-selection-page__accordion--first'>
                        <BoxedAccordion>
                            <BoxedAccordionItem
                                title={t('accordionTitleSelectProduct')}
                                asset={
                                    <Stack space={8}>
                                        {selectedProduct.productId ? (
                                            <IconCheckedFilled size={24} color={skinVars.colors.brand} />
                                        ) : (
                                            <IconCheckedRegular size={24} color={skinVars.colors.brand} />
                                        )}
                                    </Stack>
                                }
                                content={
                                    <>
                                        <div className='product-selection-page__callout product-selection-page__callout--top'>
                                            <Callout title={t('')} description={t('calloutNoteTitle')} />
                                        </div>
                                        <div className='product-selection-page__rows-container'>
                                            <RadioGroup
                                                name='product-selection'
                                                value={selectedProduct.productId}
                                                aria-label={t('chooseAProduct')}
                                            >
                                                <RowList>
                                                    {productsData.map((product) => (
                                                        <Row
                                                            key={product.id}
                                                            title={product.name}
                                                            asset={PRODUCT_ICONS[product.type]}
                                                            description={
                                                                product.isBundle
                                                                    ? product.number
                                                                    : formatMobileNumber(product.number)
                                                            }
                                                            descriptionLinesMax={1}
                                                            onPress={() =>
                                                                setSelectedProduct({
                                                                    productOfferingId: product.offeringId || '',
                                                                    productId: product.id || '',
                                                                })
                                                            }
                                                            radioValue={product.id}
                                                            extra={
                                                                product.isBundle && (
                                                                    <TextLink
                                                                        onPress={() =>
                                                                            getBundleDetails(product.bundleId || '')
                                                                        }
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {t('seeDetails')}
                                                                    </TextLink>
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </RowList>
                                            </RadioGroup>
                                        </div>
                                    </>
                                }
                            />
                        </BoxedAccordion>
                    </div>

                    <div className='product-selection-page__accordion product-selection-page__accordion--second'>
                        <BoxedAccordion>
                            <BoxedAccordionItem
                                title={t('accordionTitleHolderData')}
                                asset={
                                    <Stack space={8}>
                                        {validForm ? (
                                            <IconCheckedFilled size={24} color={skinVars.colors.brand} />
                                        ) : (
                                            <IconCheckedRegular size={24} color={skinVars.colors.brand} />
                                        )}
                                    </Stack>
                                }
                                content={
                                    <div className='product-selection-page__textfield-grid'>
                                        <div className='product-selection-page__textfield'>
                                            <TextField
                                                name='holderName'
                                                label={`${t('holderNameLabel')} ${t('mandatory')}`}
                                                autoFocus
                                                maxLength={60}
                                                value={owner.name}
                                                onChange={onChangeHolder('name')}
                                            />
                                        </div>

                                        <div className='product-selection-page__textfield'>
                                            <TextField
                                                name='holderFirstSurname'
                                                label={`${t('holderFirstSurnameLabel')} ${t('mandatory')}`}
                                                maxLength={60}
                                                value={owner.firstSurname}
                                                onChange={onChangeHolder('firstSurname')}
                                            />
                                        </div>

                                        <div className='product-selection-page__textfield'>
                                            <TextField
                                                name='holderSecondSurname'
                                                label={t('holderSecondSurnameLabel')}
                                                maxLength={60}
                                                value={owner.secondSurname ?? ''}
                                                onChange={onChangeHolder('secondSurname')}
                                            />
                                        </div>

                                        <div className='product-selection-page__textfield'>
                                            <TextField
                                                name='holderDocumentNumber'
                                                label={`${t('holderNationalIdLabel')} ${t('mandatory')}`}
                                                maxLength={20}
                                                value={owner.documentNumber}
                                                onChange={onChangeHolder('documentNumber')}
                                                error={owner.documentType === 'CIF_'}
                                                helperText={
                                                    owner.documentType === 'CIF_'
                                                        ? t('changeOwnershipCIFErrorText')
                                                        : ''
                                                }
                                            />
                                        </div>

                                        <div className='product-selection-page__textfield'>
                                            <PhoneNumberField
                                                name='holderContactPhone'
                                                label={`${t('holderContactPhoneLabel')} ${t('mandatory')}`}
                                                maxLength={20}
                                                value={owner.contactPhone}
                                                onChange={onChangeHolder('contactPhone')}
                                            />
                                        </div>
                                    </div>
                                }
                            />
                        </BoxedAccordion>
                    </div>

                    <div className='product-selection-page__callout--bottom'>
                        <Callout
                            asset={<IconInformationUserRegular color={skinVars.colors.brand} />}
                            title={t('')}
                            description={t('calloutBottomDescription')}
                        />
                    </div>
                    {openDetail && (
                        <Drawer
                            title={t('changeOwnershipDrawerTitle', { productName: selectedBundleDetails?.name })}
                            onClose={() => setOpenDetail(false)}
                            onDismiss={() => setOpenDetail(false)}
                            button={{
                                text: t('selectProduct'),
                                onPress: () => {
                                    setSelectedProduct({
                                        productId: selectedBundleDetails?.id || '',
                                        productOfferingId: selectedBundleDetails?.offeringId || '',
                                    });
                                    setOpenDetail(false);
                                },
                            }}
                            secondaryButton={{
                                text: t('close'),
                                onPress: () => setOpenDetail(false),
                            }}
                        >
                            <div className='product-selection-page__bundle'>
                                {selectedBundleDetails?.items?.map((item) => (
                                    <div key={item.productId} className='product-selection-page__bundle-item'>
                                        <span style={{ color: skinVars.colors.textPrimary }}>{item.name}</span>
                                        <div>
                                            <span style={{ color: skinVars.colors.textPrimary }}>{t('number')}: </span>
                                            <span style={{ color: skinVars.colors.textSecondary }}>
                                                {formatMobileNumber(item.number)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Drawer>
                    )}
                </ButtonFixedFooterLayout>
            </div>
        </div>
    );
};

export default ProductSelection;
